<script setup lang="ts">
import { computed } from 'vue';
import { useLanguage } from '@/lib/language';

/**
 * Which language the back office is read in.  design.md §17.1
 *
 * In the chrome rather than on a screen, because it is one choice for the whole
 * application — the catalogue, the product page, the categories and the
 * property definitions all read it. It used to be four independent `?lang=`
 * parameters with no control at all.
 *
 * Hidden when the tenant has one language: a picker with one option is furniture.
 *
 * The coverage figure sits on it because this is where somebody decides to
 * switch, and "2 of 74" is the thing that explains what they are about to see.
 */
const { lang, languages, choose } = useLanguage();

const showing = computed(() => languages.value.find((l) => l.code === lang.value) ?? null);
const gap = computed(() =>
  showing.value && showing.value.translated < showing.value.total
    ? showing.value.total - showing.value.translated
    : 0,
);
</script>

<template>
  <label v-if="languages.length > 1" class="picker">
    <span class="sr-only">Language</span>
    <select
      class="field h-7 w-full text-[12px]"
      :value="lang"
      @change="choose(($event.target as HTMLSelectElement).value)"
    >
      <option v-for="l in languages" :key="l.code" :value="l.code">
        {{ l.code.toUpperCase() }}{{ l.isDefault ? ' — default' : '' }}
      </option>
    </select>

    <span v-if="gap > 0" class="gap" :title="`${gap} products have no ${lang.toUpperCase()} name`">
      {{ gap }} untranslated
    </span>
  </label>
</template>

<style scoped>
.picker {
  display: block;
}

.gap {
  display: block;
  margin-top: 3px;
  font-size: 10px;
  color: var(--color-amber-ink);
}
</style>
